import React, { useState } from 'react';
import { certificatService } from '../../../services/certificatService';
import { useTheme } from '../../../contexts/ThemeContext';

const DeleteCertificatModal = ({ isOpen, certificat, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    const confirmationText = certificat?.pieceName || `certificat-${certificat?.id}`;
    if (confirmText !== confirmationText) {
      setError('Le texte de confirmation ne correspond pas');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await certificatService.deleteCertificat(certificat.id);

      if (result.success) {
        onSuccess();
        resetForm();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erreur lors de la suppression du certificat');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setConfirmText('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen || !certificat) return null;

  const confirmationText = certificat.pieceName || `certificat-${certificat.id}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden`}>
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-red-600 to-red-700'} px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-white bg-opacity-20'} w-10 h-10 rounded-lg flex items-center justify-center mr-3`}>
                <i className="fas fa-trash text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Supprimer le Certificat</h2>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-red-100'} text-sm`}>Cette action est irréversible</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className={`mb-6 ${isDarkMode ? 'bg-gray-900 border-l-4 border-l-red-700' : 'bg-red-50 border-l-4 border-l-red-500'} p-4 rounded-r-lg`}>
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>
                  <span className={isDarkMode ? 'text-red-300' : 'text-red-700'}>{error}</span>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className={`${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-red-50 border border-red-200'} mb-6 rounded-xl p-6`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-red-100'} w-10 h-10 rounded-lg flex items-center justify-center`}>
                    <i className="fas fa-exclamation-triangle text-red-600"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-red-900'}`}>
                    Attention : Suppression définitive
                  </h3>
                  <div className={isDarkMode ? 'text-gray-300 space-y-2' : 'text-red-700 space-y-2'}>
                    <p>Vous êtes sur le point de supprimer définitivement ce certificat :</p>
                    <div className={`${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-red-200'} rounded-lg p-4`}>
                      <div className="flex items-center">
                        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-red-50'} w-12 h-12 rounded-lg flex items-center justify-center mr-4`}>
                          <i className={`fas fa-certificate ${isDarkMode ? 'text-gray-300' : 'text-red-600'} text-lg`}></i>
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {certificat.pieceName || 'Document sans nom'}
                          </h4>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            ID: {certificat.id} • {certificat.raisonSocial}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            Créé le {formatDate(certificat.dateCertif)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Consequences */}
            <div className={`${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-amber-50 border border-amber-200'} mb-6 rounded-xl p-6`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-amber-100'} w-10 h-10 rounded-lg flex items-center justify-center`}>
                    <i className="fas fa-info-circle text-amber-600"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-amber-900'}`}>
                    Conséquences de cette suppression
                  </h3>
                  <ul className={isDarkMode ? 'text-gray-300 space-y-2' : 'text-amber-800 space-y-2'}>
                    <li className="flex items-start">
                      <i className="fas fa-chevron-right text-amber-600 mt-1 mr-2 text-xs"></i>
                      <span>Le certificat sera définitivement supprimé de la base de données</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-chevron-right text-amber-600 mt-1 mr-2 text-xs"></i>
                      <span>Le fichier physique associé ne sera pas supprimé automatiquement</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-chevron-right text-amber-600 mt-1 mr-2 text-xs"></i>
                      <span>Les références au client resteront intactes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-chevron-right text-amber-600 mt-1 mr-2 text-xs"></i>
                      <span>Cette action ne peut pas être annulée</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-chevron-right text-amber-600 mt-1 mr-2 text-xs"></i>
                      <span>L'historique et les métadonnées seront perdus</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* File Warning */}
            {certificat.piecePath && (
              <div className={`${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-orange-50 border border-orange-200'} mb-6 rounded-xl p-6`}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-orange-100'} w-10 h-10 rounded-lg flex items-center justify-center`}>
                      <i className="fas fa-file-exclamation text-orange-600"></i>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-orange-900'}`}>
                      Fichier associé
                    </h3>
                    <div className={isDarkMode ? 'text-gray-300 space-y-2' : 'text-orange-800 space-y-2'}>
                      <p>Ce certificat a un fichier associé :</p>
                      <div className={`${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-orange-200'} rounded-lg p-3 font-mono text-sm`}>
                        {certificat.piecePath}
                      </div>
                      <p className="text-sm">
                        <strong>Note importante :</strong> La suppression du certificat ne supprimera pas 
                        automatiquement le fichier physique. Vous devrez le gérer manuellement si nécessaire.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Input */}
            <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} mb-6 rounded-xl p-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Confirmation de suppression
              </h3>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                Pour confirmer la suppression, veuillez taper exactement le texte suivant :
              </p>
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg p-3 mb-4`}>
                <code className={`text-lg font-mono font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {confirmationText}
                </code>
              </div>
              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tapez le texte pour confirmer
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors font-mono ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                  placeholder={confirmationText}
                />
              </div>
              {confirmText && confirmText !== confirmationText && (
                <p className="text-red-600 text-sm mt-2">
                  <i className="fas fa-times-circle mr-1"></i>
                  Le texte ne correspond pas
                </p>
              )}
              {confirmText === confirmationText && (
                <p className="text-green-600 text-sm mt-2">
                  <i className="fas fa-check-circle mr-1"></i>
                  Confirmation valide
                </p>
              )}
            </div>

            {/* Alternative Actions */}
            <div className={`${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-blue-50 border border-blue-200'} mb-6 rounded-xl p-6`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-blue-100'} w-10 h-10 rounded-lg flex items-center justify-center`}>
                    <i className="fas fa-lightbulb text-blue-600"></i>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-blue-900'}`}>
                    Alternatives à la suppression
                  </h3>
                  <div className={isDarkMode ? 'text-gray-300 space-y-2' : 'text-blue-800 space-y-2'}>
                    <p>Avant de supprimer définitivement, considérez ces options :</p>
                    <ul className="space-y-1 ml-4">
                      <li>• Modifier les détails du certificat si les informations sont incorrectes</li>
                      <li>• Archiver le certificat pour un usage futur</li>
                      <li>• Créer une sauvegarde avant suppression</li>
                      <li>• Vérifier s'il existe des dépendances avec d'autres données</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={handleClose}
                className={`px-6 py-3 rounded-lg transition-colors font-medium ${isDarkMode ? 'text-gray-200 bg-gray-800 hover:bg-gray-700' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
              >
                <i className="fas fa-times mr-2"></i>
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={loading || confirmText !== confirmationText}
                className={`px-6 py-3 rounded-lg transition-colors font-medium flex items-center justify-center ${
                  isDarkMode
                    ? 'bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash mr-2"></i>
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteCertificatModal;